import { Fragment, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, IconButton, Tooltip } from '@mui/material';

import DateTime from 'Lib/components/common/DateTime';
import Loading from 'Lib/components/common/Loading';
import Form from 'Lib/components/form/Form';
import withForm from 'Lib/components/hoc/withForm';
import SideDrawer, { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import Components from 'Lib/constants/security/components';
import NotePermissions from 'Lib/constants/security/components/notes';
import SidebarContext from 'Lib/contexts/SidebarContext';
import useApi from 'Lib/hooks/useApi';
import useNotify from 'Lib/hooks/useNotify';
import FormDialog from 'Lib/muiComponents/dialog/FormDialog';
import LetterTemplateField from 'Lib/muiComponents/form/LetterTemplateField';
import TextField from 'Lib/muiComponents/form/TextField';
import { today } from 'Lib/utilities/datetime';
import getComponentByFieldTypeID from 'Lib/utilities/getComponentByFieldTypeID';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';
import tryParseJson from 'Lib/utilities/tryParseJson';

import ComposeForm from './ComposeForm';
import LettersContext from './context';
import { Header } from './Header';
import { Letter } from './Letter';
import { EmptyContainer, EmptyText, LetterIcon, LetterIconBackground, NewLetterButton } from './styledComponents';

export const SORT_TYPES = { NEWEST: 'Newest', OLDEST: 'Oldest' };

const WrappedLettersButton = withForm(LettersButton);

function LettersButton({ toggleDrawer, ...rest }) {
	const { mobile } = screenSize();
	const variant = mobile ? '' : 'regular';

	return (
		<>
			<Tooltip title="Letters" arrow placement="top">
				<Box sx={{ mx: { xs: 0, sm: 2 } }}>
					<IconButton variant={variant} onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.LETTERS)}>
						mail
					</IconButton>
				</Box>
			</Tooltip>
			<LettersContainer {...rest} />
		</>
	);
}

const LoadingIndicator = () => (
	<Container sx={{ textAlign: 'center', mt: 10 }}>
		<CircularProgress color="success" />
	</Container>
);

const EmptyMessage = ({ toggleCreate, hasPermission }) => (
	<EmptyContainer>
		<LetterIconBackground>
			<LetterIcon>note_alt</LetterIcon>
		</LetterIconBackground>
		<EmptyText>It seems you haven&apos;t created any letters yet.</EmptyText>
		{hasPermission(Components.Notes, NotePermissions.UpdateNotes) && (
			<NewLetterButton variant="primary" onClick={toggleCreate} sx={{ mt: 4 }}>
				Create a new Letter
			</NewLetterButton>
		)}
	</EmptyContainer>
);

const handleLetterTemplateChange = (option, form, contentProps, sidebar) => {
	const { letterObjectTypeID, objectID } = sidebar;
	const { setHtml, setInputs, renderLetterTemplate, setLoadingHtml, setLetterTemplateID, setStep } = contentProps;

	setLoadingHtml(true);

	if (option) {
		const data = {
			templateObjectTypeID: letterObjectTypeID,
			objectID,
			inputs: form ? form.values.inputs : {}
		};

		renderLetterTemplate(option.value, data)
			.then(response => {
				form.setFieldValue('html', response.data.letterTemplate.body);
				form.setFieldValue('title', `${response.data.letterTemplate.name} - ${today()}`);
				form.setFieldValue('inputs', response.data.letterTemplate.inputs);

				setStep(1);
				setLoadingHtml(false);
				setHtml(response.data.letterTemplate.body);
				setInputs(tryParseJson(response.data.letterTemplate.inputs));
				setLetterTemplateID(option.value);
				setLoadingHtml(false);
			})
			.catch(() => {
				setLoadingHtml(false);
			});
	} else {
		form.setFieldValue('html', '');
		form.setFieldValue('title', '');
		form.setFieldValue('inputs', {});

		setInputs([]);
		setHtml(null);
		setLoadingHtml(false);
	}
};

const CreateLetterModal = ({ formik, contentProps }) => {
	const { sidebar } = useContext(SidebarContext);
	const { isCreating, html, inputs, loadingHtml, step, handleSubmit, handleRender, toggleCreate, getLetterTemplates } = contentProps;
	const { letterObjectTypeID } = sidebar;
	const showRender = step == 1 && !isEmpty(inputs);

	return (
		<FormDialog
			enableReinitialize
			open={isCreating}
			toggle={toggleCreate}
			onClose={toggleCreate}
			onSubmit={showRender ? handleRender : handleSubmit}
			title={'Create a Letter'}
			ButtonProps={{ text: showRender ? 'Render Letter' : 'Create' }}
			maxWidth="sm"
			fullWidth
			sx={{
				zIndex: 1200,
				'& .MuiDialog-paper ': {
					overflow: 'visible!important'
				}
			}}
			contentSx={{ overflow: 'visible' }}>
			{() => {
				return showRender ? (
					<>
						<span>This letter template requires you to fill out the following field(s) in order to render the letter</span>
						{renderInputs(inputs)}
					</>
				) : (
					<>
						<LetterTemplateField
							id="letterTemplateID"
							name="letterTemplateID"
							label="Letter Template"
							templateObjectTypeID={letterObjectTypeID}
							api={getLetterTemplates}
							onChange={option => handleLetterTemplateChange(option, formik, contentProps, sidebar)}
						/>
						<TextField id="title" name="title" label="Letter Name" required />
						{renderEditor(loadingHtml, html, letterObjectTypeID)}
					</>
				);
			}}
		</FormDialog>
	);
};

const SendForm = ({ formik, toggleCompose, onCompose }) => {
	const { sidebar } = useContext(SidebarContext);
	const { letterAttachment } = useContext(LettersContext);
	const { letterObjectTypeID, objectID, objectTypeID } = sidebar;

	return (
		<ComposeForm
			attachments={letterAttachment}
			objectID={objectID}
			objectTypeID={objectTypeID}
			templateObjectTypeID={letterObjectTypeID}
			form={formik}
			renderAttachments={() => {
				return (
					<div className="sidebar-notes">
						<div className="note-panel">
							<div className="note-panel-header">
								<div className="note-panel-title">{letterAttachment?.title}</div>
								<div className="note-panel-footer">
									<div>
										Created at <DateTime value={letterAttachment.dateTimeCreated} />
									</div>
								</div>
							</div>
						</div>
					</div>
				);
			}}
			onSubmit={onCompose}
			onCancel={() => toggleCompose()}
		/>
	);
};

const Letters = ({ isDeleting, handleDelete, handleDownload, toggleCompose }) => {
	const { letters } = useContext(LettersContext);

	return (
		<Container disableGutters>
			{letters.map(({ file, letter }) => (
				<Letter
					key={letter.letterID}
					letter={letter}
					file={file}
					isDeleting={isDeleting}
					handleDelete={() => handleDelete(letter)}
					handleDownload={() => handleDownload(letter)}
					toggleCompose={() => toggleCompose(file, letter)}
				/>
			))}
		</Container>
	);
};

const renderInputs = inputs => {
	if (isEmpty(inputs) || inputs.length == 0) return null;

	return inputs.map?.((input, index) => (
		<Fragment key={index}>
			{getComponentByFieldTypeID(
				{
					templateFieldID: `inputs.${input.name}`,
					fieldTypeID: input.typeID,
					name: input.label,
					label: input.label,
					options: input.options
				},
				true
			)}
		</Fragment>
	));
};

const renderEditor = (loadingHtml, html, letterObjectTypeID) => {
	const {
		components: { BodyEditorField }
	} = useContext(SidebarContext);

	if (loadingHtml) return <Loading singleLoader={true} />;

	return (
		<BodyEditorField
			id="html"
			name="html"
			key={html}
			builderProps={{
				templateObjectTypeID: letterObjectTypeID,
				value: html
			}}
		/>
	);
};

function LettersContainer() {
	const { toastError, toastSuccess } = useNotify();
	const { isDrawerOpen, drawerContentTypeID } = useSelector(state => state.ui);
	const { sidebar, apis, hasPermission } = useContext(SidebarContext);
	const { objectID, letterObjectTypeID, objectTypeID } = sidebar;

	const [isSearching, setIsSearching] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	const [isComposing, setIsComposing] = useState(false);
	const [loadingHtml, setLoadingHtml] = useState(false);
	const [step, setStep] = useState(1);
	const [html, setHtml] = useState(null);
	const [inputs, setInputs] = useState([]);
	const [search, setSearch] = useState('');

	const [letter, setLetter] = useState({});
	const [letters, setLetters] = useState([]);
	const [letterAttachment, setLetterAttachment] = useState({});
	const [letterTemplateID, setLetterTemplateID] = useState(null);
	const [templates, setTemplates] = useState([]);

	const { id } = useParams();

	const getLetters = useApi(apis.getLetters);
	const getLetterTemplates = useApi(apis.getLetterTemplates);
	const createLetter = useApi(apis.createLetter);
	const deleteLetter = useApi(apis.deleteLetter);
	const downloadLetter = useApi(apis.downloadLetter);
	const renderLetterTemplate = useApi(apis.renderLetterTemplate);
	const sendEmail = useApi(apis.composeLetterEmail);

	const showAddForm = isCreating;
	const showComposeForm = isComposing;
	const showEmptyMessage = isEmpty(search) && isEmpty(letters);

	useEffect(() => {
		setIsCreating(false);
		setIsComposing(false);

		if (isDrawerOpen && drawerContentTypeID === DRAWER_CONTENT_TYPES.LETTERS) {
			fetchLetters();
			fetchLetterTemplates();
		} else {
			setLetter({});
			setLetters([]);
			setInputs([]);
			setHtml(null);
			setLetterAttachment({});
		}
	}, [isDrawerOpen]);

	const fetchLetters = () => {
		setIsLoading(true);

		getLetters(letterObjectTypeID, id, { search })
			.then(response => {
				setLetters(response.data);
				setIsLoading(false);
			})
			.catch(error => {
				toastError(error);
				setIsLoading(false);
			});
	};

	const fetchLetterTemplates = () => {
		getLetterTemplates()
			.then(response => {
				setTemplates(response.data);
			})
			.catch(error => {
				toastError(error);
			});
	};

	const searchLetters = search => {
		setSearch(search);
		setIsSearching(true);
		setIsLoading(true);

		getLetters(letterObjectTypeID, id, { search }).then(response => {
			setLetters(response.data);
			setIsSearching(false);
			setIsLoading(false);
		});
	};

	const toggleCompose = (file, letter) => {
		setLetterAttachment(file);
		setLetter(letter);
		setInputs([]);
		setHtml(null);
		setIsComposing(!isComposing);
	};

	const toggleCreate = () => {
		setIsCreating(!isCreating);
		setHtml(null);
		setInputs([]);
	};

	const handleCancel = formik => {
		setLetter({});
		setInputs([]);
		setHtml(null);
		setIsCreating(false);
		setIsComposing(false);
		setLetterAttachment({});
		formik.resetForm();
	};

	const handleDelete = letter => {
		setIsDeleting(true);

		deleteLetter(letter.letterID)
			.then(() => {
				setLetters(prevState => prevState.filter(l => l.letter.letterID !== letter.letterID));
				toastSuccess('Letter deleted successfully');
			})
			.catch(error => {
				toastError(error);
			})
			.finally(() => setIsDeleting(false));
	};

	const handleDownload = letter => {
		downloadLetter(letter.letterID)
			.then(response => {
				// Create blob link to download
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');

				link.setAttribute('href', url);
				link.setAttribute('download', response.headers['rentvine-filename']);

				// Append to html page
				document.body.appendChild(link);

				// Force download
				link.click();

				// Clean up and remove the link
				link.parentNode.removeChild(link);
			})
			.catch(error => {
				toastError(error);
			});
	};

	const handleSubmit = (values, formik) => {
		const data = {
			...values,
			objectID,
			templateObjectTypeID: letterObjectTypeID
		};

		createLetter(data)
			.then(response => {
				setLetters(prevLetters => prevLetters.push(response.data.letter));

				setIsCreating(false);
				setLetter({});
				setInputs([]);
				fetchLetters();
				toastSuccess('Letter Created Successfully');
				formik.resetForm();
			})
			.catch(error => {
				toastError(error);
			});
	};

	const handleRender = (values, form) => {
		const data = {
			templateObjectTypeID: letterObjectTypeID,
			objectID,
			inputs: form ? values.inputs : {}
		};

		renderLetterTemplate(values.letterTemplateID, data)
			.then(response => {
				setHtml(response.data.letterTemplate.body + '');
				setStep(2);

				form?.setFieldValue('html', response.data.letterTemplate.body);
			})
			.catch(error => {
				toastError(error);
			})
			.finally(() => {
				setLoadingHtml(false);
				setIsLoading(false);
				form.setSubmitting(false);
			});
	};

	const onCompose = (values, formik) => {
		const data = { ...values };

		data.objectID = objectID;
		data.objectTypeID = objectTypeID;
		data.attachments = letterAttachment;

		sendEmail(letter.letterID, data)
			.then(() => {
				setIsComposing(false);
				setLetter({});
				setInputs([]);
				setLetterAttachment({});
				toastSuccess('Email Sent Successfully');
				formik.resetForm();
			})
			.catch(error => {
				toastError(error);
			});
	};

	const title = isComposing ? 'Send Letter' : 'Letters';
	const hideHeader = showEmptyMessage || showComposeForm;

	const contentProps = {
		step,
		setStep,
		html,
		setHtml,
		inputs,
		setInputs,
		isCreating,
		setIsCreating,
		toggleCreate,
		renderLetterTemplate,
		setLoadingHtml,
		loadingHtml,
		setLetterTemplateID,
		letterTemplateID,
		handleSubmit,
		handleRender,
		getLetterTemplates
	};

	return (
		<Form
			enableReinitialize
			key={isDrawerOpen}
			initialValues={{ ...letter }}
			onSubmit={onCompose}
			render={formik => (
				<form onSubmit={formik.handleSubmit}>
					<LettersContext.Provider value={{ letters, letterAttachment, templates }}>
						<SideDrawer
							size="lg"
							drawerContentTypeID={DRAWER_CONTENT_TYPES.LETTERS}
							title={title}
							ButtonProps={{ submitText: 'Send' }}
							contentHeader={!hideHeader ? <Header isLoading={isSearching} onSearch={searchLetters} toggleCreate={toggleCreate} /> : null}
							hideFooter={!showComposeForm}
							loading={formik.isSubmitting}
							onSubmit={formik.handleSubmit}
							onCancel={() => handleCancel(formik)}>
							{isLoading ? (
								<LoadingIndicator />
							) : showAddForm ? (
								<CreateLetterModal formik={formik} contentProps={contentProps} />
							) : showEmptyMessage ? (
								<EmptyMessage toggleCreate={toggleCreate} hasPermission={hasPermission} />
							) : showComposeForm ? (
								<SendForm formik={formik} toggleCompose={toggleCompose} onCompose={onCompose} />
							) : (
								<Letters isDeleting={isDeleting} handleDelete={handleDelete} handleDownload={handleDownload} toggleCompose={toggleCompose} />
							)}
						</SideDrawer>
					</LettersContext.Provider>
				</form>
			)}
		/>
	);
}

export { WrappedLettersButton as LettersButton };
